import pick from 'lodash/pick';
import pickBy from 'lodash/pickBy';
import isEmpty from 'lodash/isEmpty';
import { types as sdkTypes } from '../../util/sdkLoader';
import { storableError } from '../../util/errors';
import {
    TRANSITION_ENQUIRY_ACCEPT,
    TRANSITION_ENQUIRY_ACCEPT_OWNER,
    TRANSITION_ENQUIRY_DECLINE,
    TRANSITION_ENQUIRY_DECLINE_OWNER,
    TX_TRANSITION_ACTOR_CUSTOMER,
    TX_TRANSITION_ACTOR_PROVIDER,
} from '../../util/transaction';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import {
    fetchCurrentUser,
    markUnreadMessageAsViewed,
    updateUserProfileInfo,
} from '../../ducks/user.duck';
import {
    loadData as inboxLoadData,
    fetchSingleChatMessages,
    sendMessage,
} from '../InboxPage/InboxPage.duck';
import { getContactInfoShared, initiateUnlocking, withdrawUserCredits } from '../../util/api';
import { parse } from '../../util/urlHelpers';

const { UUID } = sdkTypes;
export const CREDITS_NUM_TO_UNLOCK_PAGE = 30;
export const ERROR_STATUS_NOT_ENOUGH_CREDITS = 'not-enough-credits';

export const SET_INITAL_VALUES = 'app/TransactionPage/SET_INITIAL_VALUES';
export const FETCH_TRANSITIONS_REQUEST = 'app/TransactionPage/FETCH_TRANSITIONS_REQUEST';
export const FETCH_TRANSITIONS_SUCCESS = 'app/TransactionPage/FETCH_TRANSITIONS_SUCCESS';
export const FETCH_TRANSITIONS_ERROR = 'app/TransactionPage/FETCH_TRANSITIONS_ERROR';
export const HANDLE_INQUIRY_REQUEST = 'app/TransactionPage/HANDLE_INQUIRY_REQUEST';
export const HANDLE_INQUIRY_SUCCESS = 'app/TransactionPage/HANDLE_INQUIRY_SUCCESS';
export const HANDLE_INQUIRY_ERROR = 'app/TransactionPage/HANDLE_INQUIRY_ERROR';
export const PROVIDE_ANSWER_CHAT_REQUEST_MIXPANEL =
    'app/TransactionPage/PROVIDE_ANSWER_CHAT_REQUEST_MIXPANEL';
export const PROVIDE_CONSULT_CHAT_REQUEST_MIXPANEL =
    'app/TransactionPage/PROVIDE_CONSULT_CHAT_REQUEST_MIXPANEL';
export const FETCH_TX_SHARED_CONTACT_INFO_REQUEST =
    'app/TransactionPage/FETCH_TX_SHARED_CONTACT_INFO_REQUEST';
export const FETCH_TX_SHARED_CONTACT_INFO_SUCCESS =
    'app/TransactionPage/FETCH_TX_SHARED_CONTACT_INFO_SUCCESS';
export const FETCH_TX_SHARED_CONTACT_INFO_ERROR =
    'app/TransactionPage/FETCH_TX_SHARED_CONTACT_INFO_ERROR';

export const consultChatRequestMixpanel = txId => ({
    type: PROVIDE_CONSULT_CHAT_REQUEST_MIXPANEL,
    payload: { txId },
});

const initialState = {
    initialMessageFailedToTransaction: null,
    savePaymentMethodFailed: false,
    timeSlots: null,
    fetchTimeSlotsError: null,
    fetchTransitionsError: null,
    processTransitions: null,
    inquiryError: null,
    transactionRequestInProgress: false,
    sendReviewInProgress: false,
    txSharedContactInfo: {},
    fetchTxSharedContactInfoInProgress: false,
    fetchTxSharedContactInfoError: null,
};

export default function checkoutPageReducer(state = initialState, action = {}) {
    const { type, payload } = action;
    switch (type) {
        case SET_INITAL_VALUES:
            return { ...initialState, ...payload };
        case FETCH_TRANSITIONS_REQUEST:
            return { ...state, fetchTransitionsInProgress: true, fetchTransitionsError: null };
        case FETCH_TRANSITIONS_SUCCESS:
            return { ...state, fetchTransitionsInProgress: false, processTransitions: payload };
        case FETCH_TRANSITIONS_ERROR:
            return { ...state, fetchTransitionsInProgress: false, fetchTransitionsError: payload };
        case HANDLE_INQUIRY_REQUEST:
            return { ...state, transactionRequestInProgress: true, inquiryError: null };
        case HANDLE_INQUIRY_SUCCESS:
            return { ...state, transactionRequestInProgress: false };
        case HANDLE_INQUIRY_ERROR:
            return { ...state, transactionRequestInProgress: false, inquiryError: payload };
        case FETCH_TX_SHARED_CONTACT_INFO_REQUEST:
            return { ...state, fetchTxSharedContactInfoInProgress: true };
        case FETCH_TX_SHARED_CONTACT_INFO_SUCCESS:
            return {
                ...state,
                fetchTxSharedContactInfoInProgress: false,
                txSharedContactInfo: {
                    ...state.txSharedContactInfo,
                    [payload.transactionId]: payload.contactInfo,
                },
            };
        case FETCH_TX_SHARED_CONTACT_INFO_ERROR:
            return {
                ...state,
                fetchTxSharedContactInfoInProgress: false,
                fetchTxSharedContactInfoError: payload,
            };
        default:
            return state;
    }
}

const fetchTransitionsRequest = () => ({ type: FETCH_TRANSITIONS_REQUEST });
const fetchTransitionsSuccess = response => ({
    type: FETCH_TRANSITIONS_SUCCESS,
    payload: response,
});
const fetchTransitionsError = error => ({ type: FETCH_TRANSITIONS_ERROR, payload: error });
const inquiryHandlingInProgress = () => ({ type: HANDLE_INQUIRY_REQUEST });
const inquiryHandlingSuccess = txId => ({ type: HANDLE_INQUIRY_SUCCESS, payload: txId });
const inquiryHandlingError = error => ({ type: HANDLE_INQUIRY_ERROR, payload: error });
const answerChatRequestMixpanel = payload => ({
    type: PROVIDE_ANSWER_CHAT_REQUEST_MIXPANEL,
    payload,
});
const fetchTxSharedContactInfoRequest = () => ({ type: FETCH_TX_SHARED_CONTACT_INFO_REQUEST });
const fetchTxSharedContactInfoSuccess = contactInfo => ({
    type: FETCH_TX_SHARED_CONTACT_INFO_SUCCESS,
    payload: contactInfo,
});
const fetchTxSharedContactInfoError = error => ({
    type: FETCH_TX_SHARED_CONTACT_INFO_ERROR,
    payload: error,
});
export const setInitialValues = initialValues => ({
    type: SET_INITAL_VALUES,
    payload: pick(initialValues, Object.keys(initialState)),
});

/** Notify an admin about an unlock being made */
const unlockTransaction = async transactionId => {
    try {
        const res = await initiateUnlocking(transactionId);
        if (res.status !== 200) {
            throw new Error('Unable to send data');
        }
        return {};
    } catch (error) {
        return { error: error.message };
    }
};
/** Accept or decline an inquiry */
export const respondOnCustomerInquiry = (txData, { declineMessage, reason }) => async (
    dispatch,
    getState,
    sdk
) => {
    const { transactionRequestInProgress } = getState().TransactionPage;
    if (transactionRequestInProgress) return;

    const { currentUser } = getState().user;
    const { transaction } = getState().marketplaceData.entities;
    const { txId, otherParty } = txData;
    const { isSponsored } = transaction[txId.uuid].attributes.protectedData || {};
    const { txRole: otherPartyRole } = otherParty.attributes.profile.publicData;

    dispatch(inquiryHandlingInProgress());

    const isDeclineAction = declineMessage && reason;

    const transitionDictionary = {
        [TX_TRANSITION_ACTOR_PROVIDER]: {
            accept_transition: TRANSITION_ENQUIRY_ACCEPT_OWNER,
            decline_transition: TRANSITION_ENQUIRY_DECLINE_OWNER,
        },
        [TX_TRANSITION_ACTOR_CUSTOMER]: {
            accept_transition: TRANSITION_ENQUIRY_ACCEPT,
            decline_transition: TRANSITION_ENQUIRY_DECLINE,
        },
    };

    const { accept_transition, decline_transition } = transitionDictionary[otherPartyRole];

    const transition = isDeclineAction ? decline_transition : accept_transition;
    const shouldWithdrawCredits =
        !isSponsored &&
        transition !== TRANSITION_ENQUIRY_DECLINE &&
        transition !== TRANSITION_ENQUIRY_DECLINE_OWNER;

    const withdrawCreditsForCurrentTx = async () => {
        try {
            /**
             * If a tx is sponsored, the credits are withdrawn on inquiry step
             */
            const userId = currentUser.id.uuid;
            const response = await withdrawUserCredits(userId, CREDITS_NUM_TO_UNLOCK_PAGE);
            const data = await response.json();
            const { error, message, status } = data;

            if (error) {
                return dispatch(
                    inquiryHandlingError({
                        message,
                        status,
                    })
                );
            }

            dispatch(fetchCurrentUser());

            await unlockTransaction(txId.uuid);
        } catch (e) {
            return dispatch(inquiryHandlingError(e));
        }
    };

    const params = isDeclineAction
        ? {
              protectedData: {
                  isDeclined: true,
                  reason,
              },
          }
        : {};

    return sdk.transactions
        .transition(
            {
                id: txId.uuid,
                transition,
                params,
            },
            { expand: true }
        )
        .then(async response => {
            dispatch(answerChatRequestMixpanel({ txId, accepted: !isDeclineAction }));
            dispatch(addMarketplaceEntities(response));

            if (shouldWithdrawCredits) {
                await withdrawCreditsForCurrentTx();
            }

            if (declineMessage) {
                dispatch(sendMessage(txId, declineMessage));
            } else {
                // dispatch(inquiryHandlingAccept());
            }

            dispatch(inquiryHandlingSuccess(txId));

            return response;
        })
        .catch(error => {
            const isSdkError = error && error.data && Array.isArray(error.data.errors);
            if (isSdkError) {
                return dispatch(inquiryHandlingError(error.data.errors[0].title || error));
            }
            dispatch(inquiryHandlingError(error));
        });
};

const isNonEmpty = value =>
    typeof value === 'object' || Array.isArray(value) ? !isEmpty(value) : !!value;

export const fetchNextTransitions = txUuid => (dispatch, getState, sdk) => {
    if (!txUuid) return Promise.resolve();
    dispatch(fetchTransitionsRequest());

    return sdk.processTransitions
        .query({ transactionId: txUuid })
        .then(res => {
            dispatch(fetchTransitionsSuccess(res.data.data));
        })
        .catch(e => {
            dispatch(fetchTransitionsError(storableError(e)));
        });
};

export const updateChatLastActivity = txUuid => (dispatch, getState, sdk) => {
    const {
        user: { currentUser },
    } = getState();

    if (!currentUser) return;
    if (!txUuid) return;

    const {
        attributes: { profile },
    } = currentUser;

    const { publicData } = profile;

    const today = new Date();
    const chatLastActivity = {
        ...publicData.chatLastActivity,
        [txUuid]: { lastVisit: today.toString() },
    };

    const params = {
        publicData: {
            chatLastActivity,
        },
    };

    const currentUserDataUpd = { ...currentUser };
    currentUserDataUpd.attributes.profile.publicData.chatLastActivity = chatLastActivity;

    return dispatch(updateUserProfileInfo(params, currentUserDataUpd));
};

export const fetchTxSharedContactInfo = transactionId => dispatch => {
    dispatch(fetchTxSharedContactInfoRequest());

    return getContactInfoShared(transactionId)
        .then(response => (response && 'json' in response ? response.json() : response))
        .then(data => {
            if (data && data.error) {
                throw new Error(data.message || 'Failed to fetch contact information.');
            }
            dispatch(
                fetchTxSharedContactInfoSuccess({
                    transactionId,
                    contactInfo: data.otherPartyContactInfo,
                })
            );
            return data;
        })
        .catch(error => {
            dispatch(fetchTxSharedContactInfoError(storableError(error)));
            throw error;
        });
};

// loadData is a collection of async calls that need to be made
// before page has all the info it needs to render itself
export const loadData = (params, searchParams) => async (dispatch, getState) => {
    const { per_page } = parse(searchParams);

    // const inboxSearch = '';
    const inboxParams = {};

    if (per_page) inboxParams.per_page = per_page;

    const txId = new UUID(params.id);
    const state = getState().TransactionPage;
    const txRef = state.transactionRef;
    // const txRole = params.transactionRole;

    // In case a transaction reference is found from a previous
    // data load -> clear the state. Otherwise keep the non-null
    // and non-empty values which may have been set from a previous page.
    const initialValues = txRef ? {} : pickBy(state, isNonEmpty);

    dispatch(setInitialValues(initialValues));
    /**
     * inboxLoadData - fetch user's transactions;
     *
     * inboxLoadData goes first to collect all txs data;
     * after ild is successfull, the corresponding
     * tx is found inside tx page
     *
     * otherwise inboxLoadData erases this specific fields
     * in the marketplace entities data
     */
    await dispatch(inboxLoadData(inboxParams));
    // Sale / order (i.e. transaction entity in API)

    return Promise.all([
        // dispatch(inboxLoadData(inboxParams, inboxSearch)),
        // dispatch(fetchTransaction(txId, txRole)),
        dispatch(fetchSingleChatMessages(txId, 1)),
        dispatch(fetchTxSharedContactInfo(txId?.uuid)),
        /**
         * appointments are fetched at InboxPage.duck
         * as soon as transactions data is received
         */
        dispatch(fetchNextTransitions(txId?.uuid)),
        dispatch(updateChatLastActivity(txId?.uuid)),
    ]).then(() => dispatch(markUnreadMessageAsViewed(txId?.uuid)));
};
